/* eslint-disable space-before-function-paren */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ActionCreator, Store, select } from '@ngrx/store';
import { Observable, forkJoin, of, pipe } from 'rxjs';
import { catchError, exhaustMap, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { UntypedFormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InternationalCallsReportParameters } from '@ndt/report-output/international-calls/international-calls';
import { ReportDefinitions, ReportParameters } from 'generated/graphql-types';
import { reAuthenticationRequired } from 'src/app/authentication/state/authentication.actions';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { ConfirmationDialogType } from 'src/app/common-components/confirmation-dialog/models/confirmation-dialog.config';
import { ReportDownloadTypes } from '../models/report-download-types';
import { ReportParametersUnion, convertToForm } from '../report-parameter-list/models/report-parameter-union.model';
import { Reports } from '../reports.enum';
import { ReportHttpService } from '../services/report-http.service';
import { ReportService } from '../services/report.service';
import { ReportState } from './models/report-state';
import * as ReportActions from './report.actions';
import {
   CommonReportActionProps,
   ReportPageEnterActionProps,
   ReportPageEnterDrilldownActionProps,
} from './report.actions';
import { selectReport } from './reports.reducer';

@Injectable()
// eslint-disable-next-line @typescript-eslint/naming-convention
export class ReportEffects<P extends { viewMode?: any }, D> {
   submit$ = createEffect(() => {
      return this.actions$.pipe(
         this.ofActionWithState(ReportActions.reportParametersSubmitted),
         switchMap(([action, state]) => {
            return this.executeReport(state, action.reportParameters);
         })
      );
   });

   enterAndSubmit$ = createEffect(() => {
      return this.actions$.pipe(
         this.ofActionWithState(ReportActions.pageEnterAndSubmit),
         switchMap(([action, state]) => {
            if (action.allowMemorize) {
               this.reportService.onSubmit(action.rawFormValue, action.reportParameters);
            }
            return this.executeReport(state, action.reportParameters);
         })
      );
   });

   enterDrilldown$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(ReportActions.pageEnterDrilldown),
         map((action) => {
            return this.checkDrilldownReportParameterTypes(
               action.commonAndEnterReportProps,
               action.drilldownReportProps
            );
         })
      );
   });

   finalizeDrilldownParameters$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(ReportActions.drilldownParameterCheckSuccess),
         switchMap((action) => {
            return this.finalizeDrilldownReportParametersForForm(
               action.commonAndEnterReportProps,
               action.drilldownReportProps
            );
         })
      );
   });

   runDrilldownReport$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(ReportActions.drilldownParameterFinalizationSuccess),
         map((action) => {
            return ReportActions.pageEnterAndSubmit({
               ...action.commonAndEnterReportProps,
               allowMemorize: false,
               rawFormValue: convertToForm(
                  this.convertInternationCallsParameterValue(
                     action.drilldownReportProps.reportParameters,
                     action.reportParameterExtraEntityInfo,
                     action.drilldownReportProps.id
                  ),
                  this.fb
               ).getRawValue(),
               reportParameters: action.drilldownReportProps.reportParameters,
            });
         })
      );
   });

   shouldShowParametersDialog$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(ReportActions.pageEnter),
            tap(() => {
               return this.reportService.updateShowParametersDialog(true);
            })
         );
      },
      { dispatch: false }
   );

   shouldNotShowParametersDialog$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(ReportActions.pageEnterAndSubmit),
            tap(() => {
               return this.reportService.updateShowParametersDialog(false);
            })
         );
      },
      { dispatch: false }
   );

   showInvalidParametersDialog$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(ReportActions.drilldownReportFailure),
         exhaustMap((action) => {
            const dialogRef = this.confirmationDialogService.confirm({
               data: {
                  dialogType: ConfirmationDialogType.Ok,
                  header: 'Invalid Report Parameters',
                  message: 'Report Parameters are invalid. Press OK to add new Parameters for the Report.',
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               filter((confirmed) => {
                  return confirmed === true;
               }),
               map((_confirmed) => {
                  return ReportActions.pageEnter(action.commonAndEnterReportProps);
               })
            );
         })
      );
   });

   downloadExcel$ = this.createDownloadEffect(ReportActions.downloadExcel, ReportDownloadTypes.Excel);

   downloadPdf$ = this.createDownloadEffect(ReportActions.downloadPdf, ReportDownloadTypes.Pdf);

   downloadHtml$ = this.createDownloadEffect(ReportActions.downloadHtml, ReportDownloadTypes.Html);

   // saveParameters$ = createEffect(() => {
   //    return this.actions$.pipe(
   //       this.ofActionWithState(ReportActions.reportParameterSave),
   //       switchMap(([action, state]) => {
   //          const body = {
   //             name: action.name,
   //             parameters: state.parameters,
   //          };
   //          if (!state.saveUrl) {
   //             return EMPTY;
   //          }
   //          this.dialog.closeAll();
   //          return this.reportHttpService.post(state.saveUrl, body).pipe(
   //             map(() => {
   //                this.snackBar.open(`Success: ${action.name} Saved`, undefined, {
   //                   duration: 3500,
   //                });
   //                return ReportActions.reportParameterSaveSuccess({ id: state.id });
   //             }),
   //             catchError((error) => {
   //                return of(ReportActions.reportParameterSaveFailure({ id: state.id, error }));
   //             })
   //          );
   //       })
   //    );
   // });

   constructor(
      private actions$: Actions,
      private confirmationDialogService: ConfirmationDialogService,
      private dialog: MatDialog,
      private reportHttpService: ReportHttpService,
      private reportService: ReportService<P, D>,
      private snackBar: MatSnackBar,
      private store: Store,
      private fb: UntypedFormBuilder
   ) {}

   private checkDrilldownReportParameterTypes(
      commonReportProps: CommonReportActionProps & ReportPageEnterActionProps,
      drilldownReportProps: ReportPageEnterDrilldownActionProps
   ) {
      if (drilldownReportProps.isParsed && this.isReport(drilldownReportProps.baseDrilldownReport)) {
         return ReportActions.drilldownParameterCheckSuccess({
            commonAndEnterReportProps: commonReportProps,
            drilldownReportProps,
         });
      } else {
         return ReportActions.drilldownReportFailure({ commonAndEnterReportProps: commonReportProps });
      }
   }

   private executeReport(state: ReportState<P, D>, parameters: any) {
      const requests = [this.reportHttpService.post<D, P>(state.url, parameters)];
      if (state.memorizedReportId) {
         requests.push(
            this.reportHttpService.updateMemorizedReportUsageCount(state.memorizedReportId) as Observable<any>
         );
      }
      return forkJoin(requests).pipe(
         map(([reportData]) => {
            return ReportActions.reportLoadSuccess({ id: state.id, reportData });
         }),
         catchError((error) => {
            if (error?.exception === 'AuthenticationException') {
               this.store.dispatch(reAuthenticationRequired());
            }
            return of(ReportActions.reportLoadFailure({ error, id: state.id }));
         })
      );
   }

   private finalizeDrilldownReportParametersForForm(
      commonReportProps: CommonReportActionProps & ReportPageEnterActionProps,
      drilldownReportProps: ReportPageEnterDrilldownActionProps
   ) {
      let entityFieldValue$: Observable<void>;

      switch (drilldownReportProps.baseDrilldownReport) {
         case Reports.InternationalCalls:
            entityFieldValue$ = this.reportHttpService.getSingleEntityValueForParameter(
               'extensions',
               'name',
               'accountid',
               ''
            );
            break;
         default:
            throw ReportActions.drilldownReportFailure({ commonAndEnterReportProps: commonReportProps });
      }

      return entityFieldValue$.pipe(
         map((result) => {
            return ReportActions.drilldownParameterFinalizationSuccess({
               commonAndEnterReportProps: commonReportProps,
               drilldownReportProps,
               reportParameterExtraEntityInfo: result !== null ? Object.values(result[0]).toString() : '',
            });
         }),
         catchError(() => {
            return of(ReportActions.drilldownReportFailure({ commonAndEnterReportProps: commonReportProps }));
         })
      );
   }

   private createDownloadEffect<
      // eslint-disable-next-line @typescript-eslint/naming-convention
      AC extends ActionCreator<any, (props: CommonReportActionProps) => CommonReportActionProps>
   >(actionCreator: AC, downloadType: ReportDownloadTypes) {
      return createEffect(() => {
         return this.actions$.pipe(
            this.ofActionWithState(actionCreator),
            switchMap(([action, state]: [ReturnType<AC>, ReportState<P, D>]) => {
               const snackBarMessage = this.snackBar.open('Generating Report...', undefined, {
                  duration: undefined,
               });

               let download$: Observable<void>;

               switch (downloadType) {
                  case ReportDownloadTypes.Html:
                     download$ = this.reportHttpService.downloadHtml(state.url, state.parameters, state.filename);
                     break;
                  case ReportDownloadTypes.Pdf:
                     download$ = this.reportHttpService.downloadPdf(state.url, state.parameters, state.filename);
                     break;
                  case ReportDownloadTypes.Excel:
                     download$ = this.reportHttpService.downloadExcel(state.url, state.parameters, state.filename);
                     break;
               }

               return download$.pipe(
                  map(() => {
                     snackBarMessage.dismiss();
                     return ReportActions.downloadSuccess({ downloadType, id: action.id });
                  }),
                  catchError((error) => {
                     snackBarMessage.dismiss();
                     if (error?.rawError?.status === 401) {
                        this.store.dispatch(reAuthenticationRequired());
                     }
                     return of(ReportActions.downloadFailure({ downloadType, error, id: action.id }));
                  })
               );
            })
         );
      });
   }

   // eslint-disable-next-line @typescript-eslint/naming-convention
   private ofActionWithState<AC extends ActionCreator<any, (props: any) => T>, T extends CommonReportActionProps>(
      actionCreator: AC
   ) {
      return pipe(
         ofType(actionCreator),
         switchMap((a: ReturnType<AC>) => {
            return of(a).pipe(withLatestFrom(this.store.pipe(select(selectReport, a.id))));
         }),
         filter((data: [ReturnType<AC>, ReportState<P, D> | null]): data is [ReturnType<AC>, ReportState<P, D>] => {
            return data[1] != null;
         })
      );
   }

   private isReport(r: string | ReportDefinitions): r is ReportDefinitions {
      return !!ReportDefinitions[<string | ReportDefinitions>r];
   }

   // private isReportParameters(report: ReportDefinitions, parameters: unknown): boolean {
   //    switch (report) {
   //       case ReportDefinitions.InternationalCalls:
   //          return isBillableHoursDetailReportParameters(parameters);
   //       case ReportDefinitions.AverageWeeklyTicketClosure:
   //          return isAgentAverageWeeklyReportParameters(parameters);
   //       default:
   //          return false;
   //    }
   // }
   private convertInternationCallsParameterValue(
      parameterValue: InternationalCallsReportParameters,
      name: string,
      reportId: string
   ): ReportParametersUnion[] {
      let parameterUnion: ReportParametersUnion[] = [];
      parameterUnion = [
         {
            endDate: parameterValue.dateRange.endDate,
            parameterType: ReportParameters.DateRange,
            sortOrder: 0,
            startDate: parameterValue.dateRange.startDate,
         },
         {
            parameterType: ReportParameters.CallDirectionsToInclude,
            sortOrder: 1,
            value: parameterValue.callDirection.value,
         },
         {
            parameterType: ReportParameters.GroupByExtension,
            sortOrder: 2,
            value: parameterValue.groupByExtension.value,
         },
         {
            parameterType: ReportParameters.TimeZone,
            sortOrder: 3,
            value: parameterValue.timeZone?.value || '',
         },
         {
            parameterType: ReportParameters.ExtensionsToInclude,
            sortOrder: 4,
            accountId: parameterValue.extensions.accountId,
            all: parameterValue.extensions.all,
            values: [],
            names: [],
         },
      ];
      return parameterUnion;
   }
}
